import { format, formatISO } from 'date-fns';
import { catchErrorSync } from '../app/components/Common/AppErrorBoundary';
import Helper from '../app/modules/Helper';
import { datadirectsClient } from '../app/modules/DatadirectsClient';
import store from '../store';
import { getFbClientId } from '../utils/cookies';

import {
	emitSearchTrackingEvents,
	hideAndShowErros,
	sendDataToCompare,
	sendDataToPassthruForm,
	setTicketData,
	setTooltipError,
	submitForm,
	togglePriceAlertSignUpModal,
	toggleProgressLoading,
} from './baseActions';
import { saveFrontFormDataToSessionStorage } from './baseActions.utils';
import { callKayakCompareAndSaveResultsToState } from './compareActions';
import {
	HOTEL_ADD_ROOM,
	HOTEL_SET_CHECK_IN_DATE,
	HOTEL_SET_CHECK_OUT_DATE,
	HOTEL_SET_DESTINATION,
	HOTEL_SET_ROOM_TYPE,
	HOTEL_UPDATE_ROOMS,
} from './types';
import {
	emitSearchSubmissionGAEvent,
	emitSearchWithoutLocationGAEvent,
} from '../utils/ga-events-helpers';

export function getCarLocation(obj) {
	if (obj?.iata) {
		const parts = [obj.name, obj.city, obj.country].filter(Boolean);
		if (parts.length > 0) {
			return parts.join(',');
		}
		return obj.iata;
	}

	const parts = [];
	if (obj.name) {
		parts.push(obj.name);
	}
	if (obj.stateCode) {
		parts.push(obj.stateCode);
	}
	if (obj.state) {
		parts.push(obj.state);
	}
	if (obj.country) {
		parts.push(obj.country);
	}
	return parts.join(',');
}

function buildResolvedDestinationFromSelection(destination) {
	if (!destination) {
		return {
			vertical: 'hotels',
			kind: 'city',
			source: 'autosuggest',
			destcode: '',
			name: '',
			label: '',
			inputValue: '',
		};
	}

	let kind = 'city';
	if (destination.kayakType === 'ap' || destination.type === 'airport') {
		kind = 'airport';
	} else if (destination.kayakType === 'hotel' || destination.type === 'hotel') {
		kind = 'hotel';
	} else if (destination.kayakType === 'landmark' || destination.type === 'landmark') {
		kind = 'landmark';
	}

	const name = destination.name || destination.city || destination.documentId || '';
	const cityName = destination.city || '';
	const countryName = destination.country || '';
	const iataCode = kind === 'airport' ? (destination.iata || '').toUpperCase() : undefined;

	let destcode = destination.destcode || '';
	if (!destcode) {
		if (kind === 'airport' && iataCode) {
			destcode = iataCode;
		} else if (destination.ptid) {
			const id = destination.ptid.slice(1);
			const prefix = destination.ptid.charAt(0).toUpperCase();
			destcode = `${prefix}:${id}`;
		}
	}

	const label = destination.name || [destination.city, destination.country].filter(Boolean).join(', ');

	return {
		vertical: 'hotels',
		kind,
		source: 'autosuggest',
		destcode,
		name,
		label,
		inputValue: kind === 'airport' ? iataCode : name,
		placeId: destination.placeId ? String(destination.placeId) : undefined,
		cityPlaceId: destination.cityID ? String(destination.cityID) : undefined,
		cityName,
		countryName,
		countryCode: destination.countryCode,
		stateCode: destination.stateCode,
		iataCode,
		locationName: label,
	};
}

export const selectHotelDestination = (destination) =>
	catchErrorSync((dispatch) => {
		dispatch({
			type: HOTEL_SET_DESTINATION,
			payload: destination,
		});
		const errors = store.getState().base.tooltipErrors;
		delete errors['hotel-destination'];
		store.dispatch(setTooltipError(errors));
	});

export const setCheckInDate = (date) =>
	catchErrorSync((dispatch) => {
		dispatch({
			type: HOTEL_SET_CHECK_IN_DATE,
			payload: date,
		});
	});

export const setCheckOutDate = (date) =>
	catchErrorSync((dispatch) => {
		dispatch({
			type: HOTEL_SET_CHECK_OUT_DATE,
			payload: date,
		});
	});

export const setRoomType = (roomType) =>
	catchErrorSync((dispatch) => {
		dispatch({
			type: HOTEL_SET_ROOM_TYPE,
			payload: roomType,
		});
	});

export const addRoom = () =>
	catchErrorSync((dispatch) => {
		dispatch({
			type: HOTEL_ADD_ROOM,
			payload: {
				adults: 1,
				children: 0,
			},
		});
	});

export const updateRooms = (rooms) =>
	catchErrorSync((dispatch) => {
		dispatch({
			type: HOTEL_UPDATE_ROOMS,
			payload: rooms,
		});
	});

export const checkHotelDestination = (errors) => {
	const hotelDestination = store.getState().hotel.hotelDestination;
	if (Helper.isEmpty(hotelDestination)) {
		errors['hotel-destination'] = Helper.trans('validation.hotels.hotelDestination');
	}
};

export const doSearchHotels = (compare) =>
	catchErrorSync(async () => {
		compare = compare || false;
		const errors = {};

		checkHotelDestination(errors);

		store.dispatch(setTooltipError(errors));

		// GA4: fire search_without_location when destination field is empty
		if (!Helper.isEmpty(errors) && errors['hotel-destination']) {
			emitSearchWithoutLocationGAEvent();
		}

		const baseState = store.getState().base;
		if (
			Helper.isEmpty(errors) &&
			!baseState.isClosedPriceAlertSignUpModal &&
			baseState.priceAlertHandlerChecked &&
			document.body.clientWidth >= 992
		) {
			store.dispatch(togglePriceAlertSignUpModal(true));
			return;
		}
		if (Helper.isEmpty(errors)) {
			// GA4: fire search_submission with full search context
			const dataHotels = store.getState().hotel;
			const locationName =
				dataHotels.hotelDestination?.city ||
				dataHotels.hotelDestination?.name ||
				'';

			if (dataHotels.checkInDate && dataHotels.checkOutDate) {
				emitSearchSubmissionGAEvent({
					value: locationName,
					checkIn: format(new Date(dataHotels.checkInDate), 'yyyy-MM-dd'),
					checkOut: format(new Date(dataHotels.checkOutDate), 'yyyy-MM-dd'),
					rooms: dataHotels.rooms || [],
				});
				store.dispatch(emitSearchTrackingEvents());
			}
			const parsed = Helper.parseQueryString(window.location.search, true);
			let preventOpenFrontTab = false;
			if (parsed.hasOwnProperty('f') && parsed.f === '0') {
				preventOpenFrontTab = true;
			}
			let openBackTabTimeout = 500;
			if (preventOpenFrontTab) {
				openBackTabTimeout = 100;
			}

			const frontFromData = await getFormData('front');
			saveFrontFormDataToSessionStorage(frontFromData);

			setTimeout(() => {
				if (compare) {
					store.dispatch(sendDataToCompare(frontFromData));
				} else {
					if (!preventOpenFrontTab) {
						store.dispatch(submitFormFront());
					}
					setTimeout(() => {
						store.dispatch(toggleProgressLoading(true));
						store.dispatch(submitFormBack());
					}, openBackTabTimeout);
				}
			}, 10);
		} else {
			store.dispatch(hideAndShowErros());
		}
	});

export const doSearchPartners = (isKcForm = false) =>
	catchErrorSync(async () => {
		const errors = {};
		checkHotelDestination(errors);
		store.dispatch(setTooltipError(errors));
		if (!Helper.isEmpty(errors)) {
			return store.dispatch(hideAndShowErros());
		}

		const baseState = store.getState().base;
		if (
			Helper.isEmpty(errors) &&
			!baseState.isClosedPriceAlertSignUpModal &&
			baseState.priceAlertHandlerChecked &&
			document.body.clientWidth >= 992
		) {
			store.dispatch(togglePriceAlertSignUpModal(true));
			return;
		}

		const frontFromData = await getFormData('front');
		callKayakCompareAndSaveResultsToState(frontFromData, isKcForm);
	});

export const submitFormBack = () =>
	catchErrorSync(async () => {
		const form = document.getElementById('redirect-back-form');
		const data = await getFormData('back');
		store.dispatch(setTicketData(data));
		store.dispatch(submitForm(form, data));
	});

export const submitFormFront = () =>
	catchErrorSync(async () => {
		store.dispatch(sendDataToPassthruForm(await getFormData('front')));
	});

export async function getFormData(formType) {
		const parsedQuery = Helper.parseQueryString(window.location.search, true);
		const hfQueryParam = parsedQuery.hf || '';
		const hotelState = store.getState().hotel;
		const baseState = store.getState().base;
		const destination = hotelState.hotelDestination;
		const fromDate = hotelState.checkInDate;
		const toDate = hotelState.checkOutDate;
		const rooms = hotelState.rooms;

		const adultsTotal = rooms.reduce((sum, item) => sum + (item.adults || 0), 0);
		const childrenTotal = rooms.reduce((sum, item) => sum + (item.children || 0), 0);
		const fbp = getFbClientId();
		let resolvedDestination =
			destination && destination.resolvedDestination
				? destination.resolvedDestination
				: buildResolvedDestinationFromSelection(destination);

		// resolveDestination(C:…) omits Kayak places-engine id; autosuggest sets IAirportItem.placeId — merge for redirect P{id} slug.
		const placeIdFromSelection =
			destination && destination.placeId != null && String(destination.placeId).trim() !== ''
				? String(destination.placeId).trim()
				: '';
		if (resolvedDestination && placeIdFromSelection) {
			resolvedDestination = {
				...resolvedDestination,
				placeId: placeIdFromSelection,
			};
		}

		// SHP-196: For cities, Booking's bookingId() resolves "City, <region>" to a
		// random hotel instead of the city — verified on /hotels/client-redirect that
		// "Paris, Ville de Paris", "Istanbul, Istanbul Province" and "New York, New
		// York" all resolve to dest_type=hotel. The region comes from the city-prefix
		// lookup (e.g. state="Ville de Paris", an unindexed sub-region) and the SDK's
		// location-name mapping prefers regionName over countryName. Dropping it for
		// cities makes location-name fall back to "City, Country" ("Paris, France"),
		// which Booking resolves to the correct city (dest_type=city). We strip only
		// this redirect copy, leaving the stored/displayed destination untouched.
		if (
			resolvedDestination &&
			resolvedDestination.kind === 'city' &&
			resolvedDestination.regionName
		) {
			resolvedDestination = {
				...resolvedDestination,
				regionName: undefined,
			};
		}

		const locale = Helper.getLanguageCode(baseState.lng);

		const formData = await datadirectsClient.hotels.buildRedirect({
			destination: resolvedDestination,
			search: {
				checkIn: fromDate ? formatISO(fromDate) : undefined,
				checkOut: toDate ? formatISO(toDate) : undefined,
				formType: formType === 'back' ? 'back' : 'front',
				adults: adultsTotal,
				children: childrenTotal,
				rooms: rooms.length,
				roomAllocations: rooms.map((item) => ({
					adults: item.adults || 0,
					children: new Array(item.children || 0).fill(8), // Defaulting child age to 8 as per common practice in this repo
				})),
			},
			locale,
			partner: {
				...(hfQueryParam && { hf: hfQueryParam }),
				...(formType === 'front' ? { f: parsedQuery.f } : { b: parsedQuery.b }),
				...(formType === 'front' ? { fuid: parsedQuery.fuid } : { buid: parsedQuery.buid }),
			},
		});

		// SHP-196 follow-up: the regionName strip above is undone for non-English
		// locales — buildRedirect re-resolves English names by placeId and its merge
		// (english.regionName || dest.regionName) re-adds the region, so lng=de sent
		// location-name "New York, New York", which Booking resolves to the Las Vegas
		// hotel (English locales skip that lookup and correctly sent "New York,
		// United States"). Rebuild the city strings from the SDK's own English
		// city/hotel-country output so every locale posts "City, Country" exactly
		// like English does; non-city kinds keep the SDK mapping untouched.
		const cityLocationOverride = {};
		if (
			resolvedDestination &&
			resolvedDestination.kind === 'city' &&
			formData.fields &&
			formData.fields.city &&
			formData.fields['hotel-country']
		) {
			const cityCountry = `${formData.fields.city}, ${formData.fields['hotel-country']}`;
			cityLocationOverride.destination = cityCountry;
			cityLocationOverride['location-name'] = cityCountry;
		}

		// TEMP — in-app port of the datadirects-platform landmark fix (pending
		// @datadirects/datadirects-platform 7.1.51, whose publish is blocked on the org's
		// GitHub Packages billing limit). The shipped SDK (7.1.49) prepends "Hotels near "
		// to Booking's location-name for landmark/sub-city kinds, which makes Booking's
		// free-text geocoder cross-match same-named places in other countries (Big Ben →
		// New York, Lincoln Memorial → Lincoln UK) and collapse far-apart places to a hub
		// (Agatsuma Valley / Lake Shinji → Tokyo). It also sends "{name}, {cityName}" in the
		// `city` field, which blanks Booking's result page for long-tail landmarks. Only
		// Booking adds the prefix, so its presence is a safe signal: strip it from
		// location-name/destination and put the bare parent city in `city` so bookingId()
		// falls back to the city instead of a blank page. Remove once 7.1.51 is consumed.
		const sdkLocationName = formData.fields && formData.fields['location-name'];
		if (typeof sdkLocationName === 'string' && /^Hotels near /i.test(sdkLocationName)) {
			cityLocationOverride['location-name'] = sdkLocationName.replace(/^Hotels near /i, '');
			if (typeof formData.fields.destination === 'string') {
				cityLocationOverride.destination = formData.fields.destination.replace(/^Hotels near /i, '');
			}
			const bareCity = resolvedDestination && resolvedDestination.cityName;
			if (bareCity) {
				cityLocationOverride.city = bareCity;
			}
		}

		const fields = {
			...formData.fields,
			...cityLocationOverride,
			...(fbp && { fbp }),
			...(baseState.priceAlertEmail && { email: baseState.priceAlertEmail }),
		};

		return fields;
	}

export const prePopulateDataFromQueryString = () =>
	catchErrorSync(() => {
		const parsed = Helper.parseQueryString(window.location.search, true);
		if (parsed.hasOwnProperty('date-from') && parsed['date-from']) {
			let dateFrom = parsed['date-from'];
			if (Helper.isValidDateFormat(dateFrom)) {
				dateFrom = Helper.convertStringDateToDateObject(dateFrom);
				store.dispatch(setCheckInDate(dateFrom));
			}
		}
		if (parsed.hasOwnProperty('date-to') && parsed['date-to']) {
			let dateTo = parsed['date-to'];
			if (Helper.isValidDateFormat(dateTo)) {
				dateTo = Helper.convertStringDateToDateObject(dateTo);
				store.dispatch(setCheckOutDate(dateTo));
			}
		}
	});
